// ChitalishteInformationCardsPreview.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
} from "@mui/material";
import {
  Edit,
  Visibility,
  Assignment,
  Add,
  Close,
  Delete,
} from "@mui/icons-material";
import {
  fetchInformationCardsByChitalishte,
  deleteInformationCard,
  selectInformationCardsByChitalishteId,
  selectInformationCardsLoading,
  selectInformationCardsError,
  clearError,
} from "../../../store/slices/chitalishteAdminSlice";

const ChitalishteInformationCardsPreview = ({
  chitalishteId,
  chitalishteName,
  onAddCard,
  onViewAll,
}) => {
  const dispatch = useDispatch();
  const informationCards = useSelector((state) =>
    selectInformationCardsByChitalishteId(chitalishteId)(state),
  );
  const loading = useSelector(selectInformationCardsLoading);
  const error = useSelector(selectInformationCardsError);

  // Локални състояния
  const [selectedCard, setSelectedCard] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  useEffect(() => {
    if (chitalishteId) {
      dispatch(fetchInformationCardsByChitalishte(chitalishteId));
    }
  }, [dispatch, chitalishteId]);

  const cardsArray = Array.isArray(informationCards) ? informationCards : [];

  const handleViewCard = (card) => {
    setSelectedCard(card);
    setViewDialogOpen(true);
  };

  const handleEditCard = (card) => {
    // Първо отиваме в пълния мениджър
    onViewAll();
    // След това изпращаме event за редактиране
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("editInformationCard", {
          detail: { cardId: card.id, chitalishteId },
        }),
      );
    }, 300); // Даваме време на мениджъра да се зареди
  };

  const handleDeleteCard = (card) => {
    setCardToDelete(card);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (cardToDelete) {
      dispatch(deleteInformationCard({ id: cardToDelete.id, feedbackId: null }))
        .then(() => {
          setDeleteDialogOpen(false);
          setCardToDelete(null);
          // Рефреш на картите
          dispatch(fetchInformationCardsByChitalishte(chitalishteId));
        })
        .catch((err) => {
          console.error("Error deleting card:", err);
        });
    }
  };

  const formatCardData = (card) => {
    return {
      year: card.year || "Не е указана",
      totalMembersCount: card.totalMembersCount || 0,
      employeesCount: card.employeesCount || 0,
      folkloreFormations: card.folkloreFormations || 0,
      theatreFormations: card.theatreFormations || 0,
      dancingGroups: card.dancingGroups || 0,
      projectsParticipationLeading: card.projectsParticipationLeading || 0,
      projectsParticipationPartner: card.projectsParticipationPartner || 0,
      womenCount: card.womenCount || 0,
      menCount: card.menCount || 0,
      childrenCount: card.childrenCount || 0,
      youthCount: card.youthCount || 0,
      pensionersCount: card.pensionersCount || 0,
      roomsCount: card.roomsCount || 0,
      hallsCount: card.hallsCount || 0,
      stageArea: card.stageArea || 0,
      seatsCount: card.seatsCount || 0,
      musicalInstrumentsCount: card.musicalInstrumentsCount || 0,
      libraryFunds: card.libraryFunds || 0,
      currentProjects: card.currentProjects || "Няма",
      notes: card.notes || "Няма допълнителна информация",
    };
  };

  if (loading && cardsArray.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Информационни карти ({cardsArray.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={onAddCard}
            variant="contained"
          >
            Нова карта
          </Button>
          {cardsArray.length > 0 && (
            <Button
              size="small"
              startIcon={<Assignment />}
              onClick={onViewAll}
              variant="outlined"
            >
              Виж всички
            </Button>
          )}
        </Stack>
      </Box>

      {cardsArray.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", background: "#f9f9f9" }}>
          <Assignment
            sx={{ fontSize: 48, color: "text.secondary", mb: 2, opacity: 0.5 }}
          />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Няма информационни карти за "{chitalishteName}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Добавете първата информационна карта
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={onAddCard}>
            Добави информационна карта
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: "bold", width: "80px" }}>
                    Година
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "80px" }}>
                    Членове
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "80px" }}>
                    Служители
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Формации</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Проекти</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      width: "150px",
                      textAlign: "center",
                    }}
                  >
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cardsArray.slice(0, 5).map((card) => (
                  <TableRow key={card.id} hover>
                    <TableCell>
                      <Chip
                        label={card.year}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {card.totalMembersCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>{card.employeesCount || 0}</TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5}>
                        {card.folkloreFormations > 0 && (
                          <Typography variant="caption">
                            🎵 Фолклорни: {card.folkloreFormations}
                          </Typography>
                        )}
                        {card.theatreFormations > 0 && (
                          <Typography variant="caption">
                            🎭 Театрални: {card.theatreFormations}
                          </Typography>
                        )}
                        {card.dancingGroups > 0 && (
                          <Typography variant="caption">
                            💃 Танцови: {card.dancingGroups}
                          </Typography>
                        )}
                        {!card.folkloreFormations &&
                          !card.theatreFormations &&
                          !card.dancingGroups && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Няма формации
                            </Typography>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5}>
                        {card.projectsParticipationLeading > 0 && (
                          <Typography variant="caption">
                            🏆 Самостоятелни:{" "}
                            {card.projectsParticipationLeading}
                          </Typography>
                        )}
                        {card.projectsParticipationPartner > 0 && (
                          <Typography variant="caption">
                            🤝 Партньорски: {card.projectsParticipationPartner}
                          </Typography>
                        )}
                        {!card.projectsParticipationLeading &&
                          !card.projectsParticipationPartner && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Няма проекти
                            </Typography>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="Преглед">
                          <IconButton
                            size="small"
                            onClick={() => handleViewCard(card)}
                            color="info"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Редактирай">
                          <IconButton
                            size="small"
                            onClick={() => handleEditCard(card)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Изтрий">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCard(card)}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {cardsArray.length > 5 && (
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                И още {cardsArray.length - 5} информационни карти
              </Typography>
              <Button size="small" onClick={onViewAll} sx={{ mt: 1 }}>
                Виж всички
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Диалог за преглед на детайли */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              Информационна карта за {selectedCard?.year} година
            </Typography>
            <IconButton onClick={() => setViewDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCard && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Читалище: {chitalishteName}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Година
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCard.year}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Общо членове
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCard.totalMembersCount || 0}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Служители
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCard.employeesCount || 0}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Формации
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {selectedCard.folkloreFormations > 0 && (
                      <Chip
                        label={`Фолклорни: ${selectedCard.folkloreFormations}`}
                        size="small"
                      />
                    )}
                    {selectedCard.theatreFormations > 0 && (
                      <Chip
                        label={`Театрални: ${selectedCard.theatreFormations}`}
                        size="small"
                      />
                    )}
                    {selectedCard.dancingGroups > 0 && (
                      <Chip
                        label={`Танцови: ${selectedCard.dancingGroups}`}
                        size="small"
                      />
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Проекти
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {selectedCard.projectsParticipationLeading > 0 && (
                      <Chip
                        label={`Самостоятелни: ${selectedCard.projectsParticipationLeading}`}
                        size="small"
                        color="primary"
                      />
                    )}
                    {selectedCard.projectsParticipationPartner > 0 && (
                      <Chip
                        label={`Партньорски: ${selectedCard.projectsParticipationPartner}`}
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Stack>
                </Grid>

                {/* Допълнителни полета */}
                {(selectedCard.womenCount > 0 || selectedCard.menCount > 0) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Членове по пол
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      {selectedCard.womenCount > 0 && (
                        <Chip
                          label={`Жени: ${selectedCard.womenCount}`}
                          size="small"
                        />
                      )}
                      {selectedCard.menCount > 0 && (
                        <Chip
                          label={`Мъже: ${selectedCard.menCount}`}
                          size="small"
                        />
                      )}
                    </Stack>
                  </Grid>
                )}

                {selectedCard.notes &&
                  selectedCard.notes !== "Няма допълнителна информация" && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Бележки
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2">
                          {selectedCard.notes}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Затвори</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              handleEditCard(selectedCard);
            }}
          >
            Редактирай
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог за изтриване */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle color="error">Потвърждение за изтриване</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Сигурни ли сте, че искате да изтриете информационната карта за{" "}
            <strong>{cardToDelete?.year}</strong> година?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Това действие е необратимо.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отказ</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChitalishteInformationCardsPreview;
